import React, { useState } from 'react';

type OKomCollapsibleProps = {
  heading: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  /**
   * When true, the trigger is shown only on the mobile breakpoint.
   * On medium+ a plain heading is rendered and the content is always expanded.
   */
  mobileOnly?: boolean;
  /** Optional id override for aria-controls; auto-generated when omitted. */
  id?: string;
  /** Optional class added to the outer wrapper. */
  className?: string;
};

const OKomCollapsible: React.FC<OKomCollapsibleProps> = ({
  heading,
  children,
  defaultOpen = false,
  mobileOnly = false,
  id,
  className,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [autoId] = useState(() => `okom-collapsible-${Math.random().toString(36).slice(2, 10)}`);
  const contentId = id ?? autoId;

  const triggerClasses = [
    'ods-collapsible-trigger',
    'ods-collapsible-trigger--block',
    open ? 'ods-collapsible-trigger--expanded' : '',
    mobileOnly ? 'ods-collapsible-trigger--hidden-breakpoint-medium' : '',
  ].filter(Boolean).join(' ');

  const contentClasses = [
    'ods-collapsible-content',
    'ods-collapsible-content--block',
    'ods-collapsible-content--ease',
    mobileOnly ? 'ods-collapsible-content--expanded-breakpoint-medium' : '',
    open ? '' : 'ods-collapsible-content--collapsed',
  ].filter(Boolean).join(' ');

  return (
    <div className={className}>
      <h3 className="ods-status-card__heading">
        <button
          type="button"
          className={triggerClasses}
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen(o => !o)}
        >
          {heading}
        </button>
        {mobileOnly && (
          <span className="ods-none ods-block-breakpoint-medium">{heading}</span>
        )}
      </h3>
      <div id={contentId} className={contentClasses}>
        {children}
      </div>
    </div>
  );
};

export default OKomCollapsible;
